import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VoiceButton() {
  const qc = useQueryClient();
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = handleStop;
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied");
    }
  }

  function stopRecording() {
    const rec = mediaRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
    }
  }

  async function handleStop() {
    setRecording(false);
    setProcessing(true);
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];
      // Release mic
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      if (blob.size < 2000) {
        toast.error("Recording too short — try again");
        return;
      }

      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const res = await api.post("/voice/command", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const { transcript, intent, result } = res.data;

      if (result?.unknown) {
        toast.warning(`"${transcript}" — ${result.summary}`, { duration: 6000 });
        return;
      }

      // Invalidate the affected query
      if (intent?.action === "add_todo") qc.invalidateQueries({ queryKey: ["todos"] });
      if (intent?.action === "add_grocery") qc.invalidateQueries({ queryKey: ["grocery"] });
      if (intent?.action === "add_event") qc.invalidateQueries({ queryKey: ["events"] });

      // Undo handler
      const undoId =
        intent?.action === "add_todo" ? result.todo?.id :
        intent?.action === "add_grocery" ? result.grocery?.id :
        intent?.action === "add_event" ? result.event?.id : null;
      const undoEndpoint =
        intent?.action === "add_todo" ? "/todos" :
        intent?.action === "add_grocery" ? "/grocery" :
        intent?.action === "add_event" ? "/events" : null;

      toast.success(result.summary, {
        description: transcript ? `Heard: "${transcript}"` : undefined,
        duration: 7000,
        action:
          undoId && undoEndpoint
            ? {
                label: "Undo",
                onClick: async () => {
                  try {
                    await api.delete(`${undoEndpoint}/${undoId}`);
                    toast.success("Undone");
                    if (intent?.action === "add_todo") qc.invalidateQueries({ queryKey: ["todos"] });
                    if (intent?.action === "add_grocery") qc.invalidateQueries({ queryKey: ["grocery"] });
                    if (intent?.action === "add_event") qc.invalidateQueries({ queryKey: ["events"] });
                  } catch {
                    toast.error("Couldn't undo");
                  }
                },
              }
            : undefined,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Voice command failed";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }

  const onClick = recording ? stopRecording : start;
  let Icon;
  let label;
  if (processing) {
    Icon = Loader2;
    label = "Thinking…";
  } else if (recording) {
    Icon = Square;
    label = "Stop";
  } else {
    Icon = Mic;
    label = "Voice";
  }

  return (
    <button
      data-testid="voice-btn"
      onClick={onClick}
      disabled={processing}
      className="fixed bottom-6 right-6 z-50 h-16 px-6 rounded-full shadow-2xl flex items-center gap-2.5 font-mono uppercase tracking-[0.15em] text-sm font-semibold transition-all"
      style={{
        backgroundColor: recording ? "#C85A5A" : "#D6A85B",
        color: "#171514",
        boxShadow: recording
          ? "0 0 0 0 rgba(200,90,90,0.6), 0 12px 32px rgba(0,0,0,0.5)"
          : "0 12px 32px rgba(0,0,0,0.5)",
        animation: recording ? "voicePulse 1.4s ease-in-out infinite" : undefined,
      }}
      aria-label={label}
    >
      <Icon
        size={22}
        className={processing ? "animate-spin" : ""}
        strokeWidth={2.5}
      />
      <span>{label}</span>
      <style>{`
        @keyframes voicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(200,90,90,0.6), 0 12px 32px rgba(0,0,0,0.5); }
          50% { box-shadow: 0 0 0 14px rgba(200,90,90,0), 0 12px 32px rgba(0,0,0,0.5); }
        }
      `}</style>
    </button>
  );
}
